import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import * as fromCustomerStore from '@appStore/customers';
import * as fromAccountStore from '@appStore/account';
import * as fromManagersStore from '@appStore/managers';
import * as fromMeteringStore from '@appStore/metering-points';
import * as fromHelpStore from '@appStore/help';
import { filter, map, take } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { UserType, PermissionType, HelpState } from '@enums';
import { Router } from '@angular/router';
import { ManagerCompany } from '@model';
import { SxPermissionService } from '@services';

@Component({
    selector: 'app-registration-pane',
    templateUrl: './registration-pane.component.html',
    standalone: false
})
export class RegistrationPaneComponent implements OnInit, OnDestroy {
  noCustomerRegistered = false;
  noMeteringPointOnRegisteredCustomers = false;
  noAccessToMeteringPoints = false;

  isManger = false;
  isMangerAdmin = false;
  noOfManagerCompanies = 0;

  managerRegisterLink: string;
  isWhiteLabel: boolean;
  PermissionEnum = PermissionType;

  private subs = new SubSink();

  constructor(
    private customerStore: Store<fromCustomerStore.State>,
    private accountStore: Store<fromAccountStore.AccountState>,
    private managerStore: Store<fromManagersStore.State>,
    private meteringStore: Store<fromMeteringStore.State>,
    private sxPermissionService: SxPermissionService,
    private helpStore: Store<fromHelpStore.State>,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.customerStore
      .select(fromCustomerStore.getCustomersLoaded)
      .pipe(
        filter(x => !x),
        take(1)
      )
      .subscribe(x => {
        this.customerStore.dispatch(new fromCustomerStore.CustomersLoadData());
      });

    this.managerStore
      .select(fromManagersStore.getManagerCompaniesLoaded)
      .pipe(
        filter(x => !x),
        take(1)
      )
      .subscribe(x => {
        this.managerStore.dispatch(new fromManagersStore.ManagerCompaniesLoadData());
      });

    this.meteringStore
      .select(fromMeteringStore.getMeteringPointsLoaded)
      .pipe(
        filter(x => !x),
        take(1)
      )
      .subscribe(x => {
        this.meteringStore.dispatch(new fromMeteringStore.LoadMeteringPoints());
      });

    const customersAndUserInfo$ = combineLatest(
      this.customerStore.select(fromCustomerStore.getCustomerState),
      this.accountStore.select(fromAccountStore.getUserInfoState),
      this.managerStore.select(fromManagersStore.getManagerCompanies),
      this.managerStore.select(fromManagersStore.getManagerCompaniesLoaded),
      this.meteringStore.select(fromMeteringStore.getMeteringPointsState)
    ).pipe(
      map(results => ({
        customerState: results[0],
        userIfo: results[1],
        managerCompanies: results[2],
        managerCompaniesLoaded: results[3],
        meteringPoints: results[4]
      }))
    );

    // Check if user has any customer registered
    this.subs.sink = customersAndUserInfo$
      .pipe(
        filter(
          customersAnduserInfo =>
            customersAnduserInfo.customerState.loaded &&
            customersAnduserInfo.managerCompaniesLoaded &&
            customersAnduserInfo.meteringPoints.loaded
        ),
        take(1)
      )
      .subscribe(customersAnduserInfo => {
        this.isManger = customersAnduserInfo.userIfo.userType === UserType.Manager;
        this.isWhiteLabel = customersAnduserInfo.customerState.data.some(c => c.isWhiteLabel);
        this.noOfManagerCompanies = customersAnduserInfo.managerCompanies.length;

        // No Customers registered on User
        if (customersAnduserInfo.customerState.data.length === 0) {
          this.noCustomerRegistered = true;
        }

        // Access to Customer but no MeteringPoints
        if (
          customersAnduserInfo.customerState.data.length > 0 &&
          customersAnduserInfo.meteringPoints.data.length === 0
        ) {
          if (this.isManger) {
            if (
              customersAnduserInfo.managerCompanies.length > 0 &&
              this.getNumberOfMeteringPoints(customersAnduserInfo.managerCompanies) > 0
            ) {
              // Any MeteringPoints for ManagerCompanies you are connected to? - Yes ->
              this.noAccessToMeteringPoints = true;
            } else {
              // - No ->
              this.noMeteringPointOnRegisteredCustomers = true;
            }
          } else {
            // Access to Customer with no MeteringPoints
            this.noMeteringPointOnRegisteredCustomers = true;
          }
        }

        if (this.isManger && customersAnduserInfo.managerCompanies.length === 1) {
          this.isMangerAdmin = this.sxPermissionService.securityTrim(
            undefined,
            [PermissionType.ManagePermissionManagers],
            undefined,
            customersAnduserInfo.managerCompanies[0].id
          );
          if (this.isMangerAdmin) {
            // If Admin then got to Add additional Customer to ManagerCompany
            this.managerRegisterLink =
              '/secure/register/' + customersAnduserInfo.managerCompanies[0].id + '/additional';
          } else {
            // Otherwise go to ManagerCompany
            this.managerRegisterLink = '/secure/managers/' + customersAnduserInfo.managerCompanies[0].id;
          }
        } else if (this.isManger && customersAnduserInfo.managerCompanies.length > 1) {
          this.managerRegisterLink = '/secure/managers';
        } else if (this.isManger && customersAnduserInfo.managerCompanies.length === 0) {
          this.managerRegisterLink = '/secure/register/additional';
        }
      });
  }

  getNumberOfMeteringPoints(managerCompanies: ManagerCompany[]) {
    let totalNumberOfMPs = 0;
    managerCompanies.map(x => (totalNumberOfMPs = totalNumberOfMPs + x.numberOfMeteringPoints));
    return totalNumberOfMPs;
  }

  navigateToManagerAdmin() {
    this.router.navigate([this.managerRegisterLink]);
  }

  openSupportPage() {
    this.helpStore.dispatch(new fromHelpStore.HelpSetState(HelpState.support));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
